import { ApplicationMetadata, Query, QueryResults, VizType } from '@kentik/ui-shared/query/query.types';
import { action, computed, observable, ObservableMap } from 'mobx';
import { DEFAULT_QUALITATIVE_PALETTES } from 'app/stores/colors/Colors';
import { getMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { merge } from 'lodash';
import { getRollupKey } from '@kentik/ui-shared/query/query';
import { getApplicationMetadata } from '@kentik/ui-shared/query/queryUtils';
import { getMirroredQuery } from '@kentik/ui-shared/query/mirror';
import QueryRequest, { QueryStatus } from './QueryRequest';

export interface ThresholdStep {
  value: number | null; // null for base step
  color: string; // hex color
}

export type ThresholdMode = 'absolute' | 'percentage';
export type ThresholdDisplay = 'off' | 'line' | 'dashed-line' | 'area' | 'line+area' | 'dashed-line+area';

export interface ThresholdsConfig {
  enabled: boolean;
  mode: ThresholdMode;
  display: ThresholdDisplay;
  colorSeries: boolean;
  steps: ThresholdStep[];
}

export interface QuerySettings {
  chart: Partial<{
    enable: boolean;
    type: VizType;
    colors: keyof typeof DEFAULT_QUALITATIVE_PALETTES;
    legend: Partial<{
      enable: boolean;
      position: 'bottom' | 'right' | 'left';
      mode: 'table' | 'condensed';
      showRollups: boolean;
      showEventsView: boolean;
    }>;
    xAxis: {
      /** 'UTC', 'Local', or an IANA timezone string like 'America/New_York'. Defaults to 'UTC' */
      timezone?: string;
    };
    yAxis: {
      type: 'linear' | 'logarithmic';
      metric?: string;
      mirrorMode?: boolean;
      syncAxes?: boolean;
    };
    thresholds?: ThresholdsConfig;
  }>;
  // table.enable removed — replaced by legend.enable + legend.mode
}

export interface QueryOptions {
  results?: QueryResults;
  applicationMetadata?: Partial<ApplicationMetadata>;
}

export default class QueryModel {
  @observable showDebugDialog: boolean = false;

  @observable dirty: boolean = true;

  runSequence = 0;

  forceRun: boolean = false;

  @observable.ref query: Query;

  @observable.ref settings: QuerySettings;

  mainQueryRequest = new QueryRequest({ queryModel: this, type: 'main' });

  mirrorQueryRequest = new QueryRequest({ queryModel: this, type: 'mirror' });

  /** Override metadata merged into URL-derived application_metadata on each run. */
  applicationMetadata?: Partial<ApplicationMetadata>;

  /**
   * Series interaction state (visibility, hover).
   * Owned by QueryModel because it is the sole shared MobX observable in the UDE.
   * If QueryModel accumulates more UI concerns in the future, consider extracting
   * these fields into a dedicated SeriesInteractionState child model.
   */

  /** Series visibility map: `row.id` → visible. Absent = visible (default). */
  @observable seriesVisibility: ObservableMap<string, boolean> = observable.map();

  /** ID of the currently hovered series row, or null. */
  @observable hoveredSeriesId: string | null = null;

  /**
   * Client-side override for the number of series to display.
   * Used by the chart renderer to slice results without re-running the query.
   */
  @observable vizLimit: number | null = null;

  /** Resolved series limit: vizLimit override → query.viz.limit → 10 */
  @computed get effectiveVizLimit(): number {
    return this.vizLimit ?? this.query.viz?.limit ?? 10;
  }

  constructor(query: Query, settings: QuerySettings, options?: QueryOptions) {
    this.query = query;
    this.settings = settings;
    this.applicationMetadata = options?.applicationMetadata;

    this.queryRequests.forEach((request) => {
      request.query = query;

      let results: QueryResults | undefined;
      if (request.type === 'main') {
        results = options?.results;
      }

      if (results) {
        this.dirty = false;
        request.results.replace(results);
        request.rawRows = results.rawRows ?? [];
        request.rawColumnKeys = results.rawColumnKeys ?? [];
        request.rawEventsCollection.replace(request.rawRows);
        request.status = 'done';
      }
    });
  }

  isQueryRequestActive(request: QueryRequest): boolean {
    const { hasRun, type } = request;
    if (type === 'main') {
      return true;
    }
    if (type === 'mirror') {
      return this.settings.chart.yAxis?.mirrorMode === true && hasRun;
    }
    return hasRun;
  }

  @computed get queryRequests(): QueryRequest[] {
    return [this.mainQueryRequest, this.mirrorQueryRequest];
  }

  @computed get activeQueryRequests(): QueryRequest[] {
    return this.queryRequests.filter((q) => this.isQueryRequestActive(q));
  }

  @computed get hasRun() {
    return this.activeQueryRequests.some((q) => q.hasRun);
  }

  @computed get isLoading() {
    return this.activeQueryRequests.some((q) => q.isLoading);
  }

  @computed get hasError() {
    return this.activeQueryRequests.some((q) => q.hasError);
  }

  @computed get hasResults() {
    return this.activeQueryRequests.some((q) => q.hasResults);
  }

  @computed get fullyLoaded() {
    return this.activeQueryRequests.every((q) => q.fullyLoaded);
  }

  @computed get status(): QueryStatus {
    if (!this.hasRun) {
      return 'init';
    }
    if (this.hasError) {
      return 'error';
    }
    if (this.isLoading && this.hasResults) {
      return 'partial';
    }
    if (this.isLoading) {
      return 'running';
    }
    return 'done';
  }

  @computed get lastResultUpdate() {
    return this.activeQueryRequests.reduce((max, q) => Math.max(max, q.results.lastUpdated ?? 0), 0);
  }

  /** The metric charted in the primary positive y-axis */
  @computed get primaryMetric(): string {
    const selected = this.settings.chart.yAxis?.metric;
    if (selected) {
      // Check if it's a rollup key (fn_metricName) matching a configured rollup
      const matchingRollup = this.query.rollups.find((r) => getRollupKey(r) === selected);
      if (matchingRollup) {
        return matchingRollup.metric;
      }

      // Check if it's a plain metric name (backwards compat)
      const isRegularMetric = this.query.metrics.some((m) => m.name === selected);
      if (isRegularMetric) {
        return selected;
      }
    }

    return this.query.metrics[0]?.name ?? '';
  }

  /** The rollup charted in aggregate charts */
  @computed get primaryRollupKey(): string {
    const selected = this.settings.chart.yAxis?.metric;
    if (selected) {
      // Check if it matches a configured rollup
      const matchingRollup = this.query.rollups.find((r) => r.metric === selected);
      if (matchingRollup) {
        return getRollupKey(matchingRollup);
      }
    }
    // Fallback: first rollup key, or plain metric name
    const firstRollup = this.query.rollups[0];
    if (firstRollup) {
      return getRollupKey(firstRollup);
    }

    return this.query.metrics[0]?.name ?? '';
  }

  @computed get allRowIds(): string[] {
    const ids = new Set<string>();
    this.activeQueryRequests.forEach((request) => {
      request.results.rows.forEach((row) => {
        ids.add(row.id);
      });
    });
    return Array.from(ids.values());
  }

  @computed get chartedRowIds(): string[] {
    return this.allRowIds.filter((id) => this.isRowCharted(id));
  }

  @computed get seriesVisibilityMap(): Map<string, boolean> {
    return new Map(this.chartedRowIds.map((id) => [id, this.isSeriesVisible(id)]));
  }

  /** Toggle visibility for a single series row.
   *  Deletes the map entry when restoring to visible (absent = visible default),
   *  so `seriesVisibility.size` accurately signals "active selection" for live updates. */
  @action toggleSeriesVisibility(rowId: string) {
    const current = this.seriesVisibility.get(rowId);
    if (current === false) {
      this.seriesVisibility.delete(rowId);
    } else {
      this.seriesVisibility.set(rowId, false);
    }
  }

  /** Set visibility for a specific series row (used by cmd+click and shift+click).
   *  Deletes the map entry when setting visible, so only hidden rows occupy the map. */
  @action setSeriesVisibility(rowId: string, visible: boolean) {
    if (visible) {
      this.seriesVisibility.delete(rowId);
    } else {
      this.seriesVisibility.set(rowId, false);
    }
  }

  /** Show all series (reset visibility map). */
  @action showAllSeries() {
    this.seriesVisibility.clear();
  }

  @action hideAllSeries() {
    this.chartedRowIds.forEach((rowId) => {
      this.seriesVisibility.set(rowId, false);
    });
  }

  /** Check if a series is visible (absent from map = visible). */
  isSeriesVisible(rowId: string): boolean {
    return this.seriesVisibility.get(rowId) !== false;
  }

  @computed get colors(): string[] {
    return DEFAULT_QUALITATIVE_PALETTES[this.settings.chart.colors ?? 'default'];
  }

  @computed get seriesColors(): Map<string, string> {
    const { colors } = this;
    const seriesColors = new Map<string, string>();
    let colorIndex = 0;
    this.allRowIds.forEach((rowId) => {
      if (this.isRowCharted(rowId)) {
        seriesColors.set(rowId, colors[colorIndex % colors.length]);
        colorIndex += 1;
      }
    });
    return seriesColors;
  }

  getSeriesColor(rowId: string): string | undefined {
    return this.seriesColors.get(rowId);
  }

  @action setHoveredSeries(rowId: string | null) {
    this.hoveredSeriesId = rowId;
  }

  @action setChartedRowIds(idMap: Record<string, string[]>) {
    this.queryRequests.forEach((request) => {
      request.chartedRowIds = new Set(idMap[request.type] || []);
    });
  }

  /** Check if a row has a corresponding chart series/point. */
  isRowCharted(rowId: string): boolean {
    return this.activeQueryRequests.some((request) => request.isRowCharted(rowId));
  }

  buildApplicationMetadata(query: Query = this.query): ApplicationMetadata {
    const baseMetadata = getApplicationMetadata({ query }, window.location.pathname);
    return {
      ...baseMetadata,
      ...(this.applicationMetadata || {}),
      data: {
        ...baseMetadata.data,
        ...(this.applicationMetadata?.data || {})
      }
    };
  }

  @action async run(queryOverride?: Partial<Query>) {
    const runSequence = this.runSequence + 1;
    this.runSequence = runSequence;
    this.dirty = false;
    this.forceRun = false;

    // Reset series interaction state — user is fetching new data.
    this.seriesVisibility.clear();
    this.hoveredSeriesId = null;

    // set the chart type on the query, and apply any overrides
    const query = merge(
      {
        viz: { type: this.settings.chart.type }
      },
      this.query,
      queryOverride || {}
    ) as Query;

    // TODO I think we have another way of forcing a specific rollup function in UDE? But I can't track it down again
    if (query.viz?.type === 'timelineView') {
      query.rollups.forEach((rollup) => {
        rollup.fn = 'min';
      });
    }

    // Derive application_metadata from URL path and merge any overrides (e.g. panel_id).
    const application_metadata = this.buildApplicationMetadata(query);

    const runs = [this.mainQueryRequest.run(query, application_metadata)];

    if (this.settings.chart.yAxis?.mirrorMode) {
      const measurement = await getMeasurement(query.measurement);
      if (this.runSequence !== runSequence) {
        // a new run was started while awaiting, bail out
        return;
      }
      if (measurement) {
        const mirrorQuery = getMirroredQuery(query, measurement);
        runs.push(this.mirrorQueryRequest.run(mirrorQuery, application_metadata));
      } else {
        console.warn(`Unable to mirror query: measurement "${query.measurement}" not found in dictionary.`);
        this.mirrorQueryRequest.status = 'error';
      }
    } else {
      this.mirrorQueryRequest.reset();
    }

    await Promise.all(runs);
  }

  cleanup() {
    this.queryRequests.forEach((request) => request.cleanup());
    this.runSequence = 0;
  }

  @action
  handleShowDebugDialog = () => (this.showDebugDialog = true);

  @action
  hideDebugDialog = () => (this.showDebugDialog = false);
}
